import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Check } from "lucide-react";
import { api, formatApiError, resolveMediaUrl } from "@/lib/api";
import FileUpload from "@/components/admin/FileUpload";
import { asBi, asBiList, bi, biSet, emptyBi } from "@/lib/bilingual";

const empty = {
  name: emptyBi(),
  slug: "",
  category: emptyBi("Minuman Serbuk", "Powder Drink"),
  image: "",
  description: emptyBi(),
  benefits: [],
  specs: [],
  order: 0,
  published: true,
};

const AdminProducts = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [editLang, setEditLang] = useState("id");

  const load = () => api.get("/products").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(empty); setEditLang("id"); };
  const openEdit = (p) => {
    setEditing(p.id);
    setEditLang("id");
    setForm({
      ...p,
      name: asBi(p.name),
      category: asBi(p.category, "Powder Drink"),
      description: asBi(p.description),
      benefits: asBiList(p.benefits || []),
      specs: asBiList(p.specs || []),
    });
  };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    try {
      const payload = {
        name: form.name,
        slug: form.slug || "",
        category: form.category,
        image: form.image || "",
        description: form.description || emptyBi(),
        benefits: form.benefits || [],
        specs: form.specs || [],
        order: Number(form.order || 0),
        published: !!form.published,
      };
      if (editing === "new") await api.post("/products", payload);
      else await api.put(`/products/${editing}`, payload);
      toast.success("Berhasil disimpan");
      close(); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail || e.response?.data));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus produk ini?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Terhapus");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-zinc-900">Produk</h1>
          <p className="mt-1 text-sm text-zinc-500">Kelola katalog produk minuman serbuk (Indonesia &amp; English).</p>
        </div>
        <button onClick={openNew} data-testid="new-product" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-maroon-800 text-white font-semibold hover:bg-maroon-900">
          <Plus size={16} /> Tambah Produk
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="p-4">Nama</th><th className="p-4">Kategori</th><th className="p-4">Urutan</th><th className="p-4">Publish</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-zinc-100">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {p.image && <img src={resolveMediaUrl(p.image)} alt={bi(p.name, "id")} className="w-10 h-10 rounded-lg object-cover" />}
                    <div>
                      <div className="font-medium text-zinc-900">{bi(p.name, "id")}</div>
                      {bi(p.name, "en") && bi(p.name, "en") !== bi(p.name, "id") && (
                        <div className="text-xs text-zinc-500">{bi(p.name, "en")}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="p-4 text-zinc-600">{bi(p.category, "id")}</td>
                <td className="p-4 text-zinc-600">{p.order}</td>
                <td className="p-4">{p.published ? <span className="text-maroon-700 font-semibold">Aktif</span> : <span className="text-zinc-400">Draft</span>}</td>
                <td className="p-4 text-right">
                  <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="p-2 rounded-lg hover:bg-zinc-100"><Pencil size={14} /></button>
                  <button onClick={() => del(p.id)} data-testid={`delete-${p.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-zinc-500">Belum ada produk.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white flex items-center justify-between p-6 border-b border-zinc-200">
              <h3 className="text-xl font-display font-bold">{editing === "new" ? "Tambah Produk" : "Edit Produk"}</h3>
              <div className="flex items-center gap-2">
                <div className="flex rounded-full overflow-hidden border border-zinc-200 text-xs font-bold">
                  <button type="button" onClick={() => setEditLang("id")} className={`px-3 py-1.5 ${editLang === "id" ? "bg-maroon-800 text-white" : "bg-white text-zinc-600"}`}>ID</button>
                  <button type="button" onClick={() => setEditLang("en")} className={`px-3 py-1.5 ${editLang === "en" ? "bg-maroon-800 text-white" : "bg-white text-zinc-600"}`}>EN</button>
                </div>
                <button onClick={close} className="p-2 rounded-lg hover:bg-zinc-100"><X size={18} /></button>
              </div>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-4">
              <Input label={`Nama * (${editLang.toUpperCase()})`} value={bi(form.name, editLang)} onChange={(v) => setForm({ ...form, name: biSet(form.name, editLang, v) })} testid="pf-name" />
              <Input label={`Kategori (${editLang.toUpperCase()})`} value={bi(form.category, editLang)} onChange={(v) => setForm({ ...form, category: biSet(form.category, editLang, v) })} />
              <div className="sm:col-span-2">
                <FileUpload label="Gambar Produk" value={form.image} onChange={(v) => setForm({ ...form, image: v })} folder="products" aspect="wide" testid="pf-image-upload" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-zinc-800 mb-2">Deskripsi ({editLang.toUpperCase()})</label>
                <textarea rows={4} value={bi(form.description, editLang)} onChange={(e) => setForm({ ...form, description: biSet(form.description, editLang, e.target.value) })}
                  className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-maroon-700 focus:ring-2 focus:ring-maroon-100 outline-none" />
              </div>
              <ListInput label={`Benefit (1 per baris, ${editLang.toUpperCase()})`} value={form.benefits} lang={editLang} onChange={(v) => setForm({ ...form, benefits: v })} />
              <ListInput label={`Spesifikasi (1 per baris, ${editLang.toUpperCase()})`} value={form.specs} lang={editLang} onChange={(v) => setForm({ ...form, specs: v })} />
              <Input label="Urutan" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
              <div className="flex items-center gap-2 pt-8">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} id="pub" />
                <label htmlFor="pub" className="text-sm">Tampilkan di website</label>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white flex justify-end gap-3 p-6 border-t border-zinc-200">
              <button onClick={close} className="px-5 py-2.5 rounded-full border border-zinc-200 text-zinc-700 font-semibold">Batal</button>
              <button onClick={save} data-testid="save-product" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-maroon-800 text-white font-semibold hover:bg-maroon-900"><Check size={16} /> Simpan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Input = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="block text-sm font-semibold text-zinc-800 mb-2">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-maroon-700 focus:ring-2 focus:ring-maroon-100 outline-none" />
  </div>
);

const ListInput = ({ label, value, onChange, lang }) => {
  const text = (value || []).map((item) => bi(item, lang)).join("\n");
  const apply = (raw) => {
    const lines = raw.split("\n");
    const prev = value || [];
    return lines.map((line, i) => biSet(prev[i] || emptyBi(), lang, line)).filter((item) => bi(item, "id").trim() !== "" || bi(item, "en").trim() !== "");
  };
  return (
    <div className="sm:col-span-2">
      <label className="block text-sm font-semibold text-zinc-800 mb-2">{label}</label>
      <textarea rows={4} value={text} onChange={(e) => onChange(apply(e.target.value))}
        className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-maroon-700 focus:ring-2 focus:ring-maroon-100 outline-none font-mono text-sm" />
    </div>
  );
};

export default AdminProducts;
